import React, { ReactElement } from 'react';
import classnames from 'classnames';
import { FieldError, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledSelect } from '../../../../../../components';
import { ControlledToggleButtonGroup } from '../../../../../../components/Form/ControlledToggleButtonGroup';
import { ControlledMultipleSelect } from '../../../../../../components/Form/ControlledMultipleSelect';
import { SelectOption } from '../../../../../../components/CiscoUi';
import { PrimaryButton } from '../../../../../../components/Buttons/PrimaryButton';
import { PLEASE_SELECT_OPTION } from '../../../../../../constants/form';
import {
  getNetworkOptions,
  getTrunkedNetworkOptions,
  getTrunkedNetworkUids,
  resolveTrunkedNetworksSelection,
} from '../../../components/VirtualMachineEntities/Configure/Utils';
import { AddNetworkFormData } from './form-validation';
import styles from './AddNetwork.module.css';
import { useFormInit } from './hooks/use-form-init';
import { useTrunkToggleValidation } from './hooks/use-trunk-toggle-validation';

export interface AddNetworkProps {
  interfaces: SelectOption<string>[];
  networks: Network[];
  onAddClick: (fields: AddNetworkFields, networks: Network[]) => void;
}

export function AddNetwork({
  interfaces,
  networks,
  onAddClick,
}: AddNetworkProps): ReactElement {
  const { t } = useTranslation();

  const { control, errors, formState, handleSubmit, reset, trigger, watch } =
    useFormInit();

  const isTrunked = useTrunkToggleValidation({ trigger, watch });
  const { isDirty, isValid } = formState;

  const onSubmit: SubmitHandler<AddNetworkFormData> = (formData) => {
    onAddClick(formData, networks);
    reset();
  };

  const handleTrunkedNetworksChange = (next: string[]) =>
    resolveTrunkedNetworksSelection(
      control.getValues().trunkedNetworks ?? [],
      next,
      getTrunkedNetworkUids(networks),
    );

  const interfaceOptions = [PLEASE_SELECT_OPTION, ...interfaces];

  return (
    <>
      <div className={styles.trunkedRow}>
        <ControlledToggleButtonGroup
          control={control}
          name="trunked"
          options={{
            left: {
              label: t('networks.trunk.access'),
              value: false,
            },
            right: {
              label: t('networks.trunk.label'),
              value: true,
            },
          }}
        />
      </div>
      <div className="row flex-middle">
        <div className="col-3">
          <ControlledSelect
            control={control}
            label="Interface"
            horizontal={false}
            options={interfaceOptions}
            name="interface"
            loading={false}
            testId="add-hardware-networking-interface-select"
            required={true}
            error={errors?.interface?.message}
          />
        </div>
        <div className="col-4">
          {isTrunked ? (
            <ControlledMultipleSelect
              control={control}
              name="trunkedNetworks"
              label={t('networks.trunk.networkOrVlans')}
              horizontal={false}
              options={getTrunkedNetworkOptions(networks)}
              customOnChange={handleTrunkedNetworksChange}
              required
              testId="add-hardware-networking-trunked-networks"
              error={
                (errors.trunkedNetworks as FieldError | undefined)?.message
              }
            />
          ) : (
            <ControlledSelect
              control={control}
              label={t('networks.trunk.networkOrVlans')}
              horizontal={false}
              options={getNetworkOptions(networks)}
              name="network"
              loading={false}
              testId="add-hardware-networking-network-select"
              required={true}
              error={errors?.network?.message}
            />
          )}
        </div>
        <PrimaryButton
          className={classnames(styles.button)}
          type="button"
          disabled={!isDirty || !isValid}
          onClick={handleSubmit(onSubmit)}
          testId="add-network-add-button"
        >
          {t('buttons.add')}
        </PrimaryButton>
      </div>
    </>
  );
}
